"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { cn } from "@/lib/utils";
export default function SupportLocalSound() {
  return <section data-section-id="222490" id="start-your-estimate" className="relative bg-background text-foreground py-24 md:py-32 overflow-hidden light">
      {}
      <div className="container relative z-10 mx-auto px-4 max-w-3xl">
        <div className="mb-12 text-center">
          <div className="inline-block mb-8">
            <h3 className="text-primary font-default font-extrabold tracking-tighter uppercase !text-center border-4 border-primary px-6 py-2 shadow-lg !text-[35px]">EMANUEL   CONSTRUCTION   GROUP</h3>
          </div>
          
          <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto font-default">North Carolina Licensed General Contractor #109136<br />Commercial and Residential<br />Fill out the form to connect with us!</p>
        </div>

        <FormProvider formKey="estimate_form_home" formId="estimate_form_home" sectionName="Support Local Sound" className="border-2 border-secondary p-8 md:p-12 bg-card/80 backdrop-blur-sm shadow-2xl" defaultValues={{
        name: "",
        email: "",
        phone: "",
        support_type: "",
        message: "",
        subscribe_newsletter: false
      }} validationRules={{
        name: {
          required: "Name is required"
        },
        email: {
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        },
        phone: {
          pattern: {
            value: /^[0-9\-\+\s\(\)]*$/,
            message: "Please enter a valid phone number"
          }
        },
        support_type: {
          required: "Please select a service type"
        }
      }} submitText="Send Message" successMessage="Thank you! Our project managers will review your request and contact you shortly.">
          <SupportForm />
        </FormProvider>
      </div>
    </section>;
}
function SupportForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const minimalistInputClass = "border-0 border-b-2 border-border rounded-none px-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:border-primary h-12 text-xl transition-all duration-300 placeholder:text-muted-foreground/40 font-default";
  if (isSubmitted && !submitError) {
    return <div className="p-8 text-center space-y-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary text-primary-foreground mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
        </div>
        <h3 className="font-default font-bold text-2xl uppercase tracking-tight text-foreground">Request Received</h3>
        <p className="text-muted-foreground text-lg font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-8 border-primary text-primary hover:bg-primary hover:text-primary-foreground uppercase tracking-widest font-bold transition-colors">
          Submit Another Request
        </Button>
      </div>;
  }
  const supportOptions = ["Concrete", "Fencing", "Painting", "Epoxy", "Renovations", "Doors & Windows"];
  const checkboxSvgData = "data:image/svg+xml;utf8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22white%22%20stroke-width%3D%224%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%2220%206%209%2017%204%2012%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="uppercase tracking-widest text-xs text-primary font-bold font-default">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" className={minimalistInputClass} {...field} />
              </FormControl>
              <FormDescription className="text-xs font-default">Required for project documentation</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="uppercase tracking-widest text-xs text-primary font-bold font-default">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jsmith@example.com" className={minimalistInputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="uppercase tracking-widest text-xs text-primary font-bold font-default">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(336) 000-0000" className={minimalistInputClass} {...field} />
              </FormControl>
              <FormDescription className="text-xs font-default">For scheduling site inspections</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="support_type" render={({
        field
      }) => <FormItem>
              <FormLabel className="uppercase tracking-widest text-xs text-primary font-bold font-default">Service Category</FormLabel>
              <FormControl>
                <select className={cn(minimalistInputClass, "w-full appearance-none cursor-pointer")} {...field}>
                  <option value="" disabled className="bg-card text-muted-foreground">Select Service</option>
                  {supportOptions.map((opt, i) => <option key={i} data-index={i} value={opt} className="bg-card text-foreground py-2">
                      {opt}
                    </option>)}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="uppercase tracking-widest text-xs text-primary font-bold font-default">Project Details</FormLabel>
            <FormControl>
              <textarea placeholder="Describe your project scope, location, and timeline..." className={cn(minimalistInputClass, "w-full min-h-[100px] py-3 resize-y")} {...field} />
            </FormControl>
            <FormDescription className="text-xs font-default">Include any specific requirements or site conditions</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="subscribe_newsletter" render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-4">
            <FormControl>
              <input type="checkbox" className={`mt-1 h-5 w-5 shrink-0 appearance-none border-2 border-primary bg-transparent checked:bg-primary checked:bg-[url('${checkboxSvgData}')] bg-center bg-no-repeat transition-colors cursor-pointer`} checked={field.value} onChange={field.onChange} onBlur={field.onBlur} name={field.name} ref={field.ref} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="cursor-pointer text-base uppercase tracking-tight font-bold font-default">
                Receive Maintenance Tips
              </FormLabel>
              <FormDescription className="font-default">
                Get seasonal construction and property care advice
              </FormDescription>
            </div>
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm font-bold font-default">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-default font-extrabold uppercase tracking-widest transition-all duration-400 bg-primary text-primary-foreground hover:bg-foreground hover:text-background mt-8">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing...
          </> : 'Send Message'}
      </Button>
    </Form>;
}